/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {OfferBasicSchema} from 'components/Offers/schemas/offerBasicSchema'
import {useTranslation} from 'react-i18next'
import {z} from 'zod'
import {useMemo} from 'react'
import {isStrictIsoDate} from 'utils/formValidation'

export { OfferBasicSchema }

export const useOfferBasicSchema = () => {
    const { t } = useTranslation()

    return useMemo(
        () =>
            z.object({
                name: z.string().trim().optional().nullable(),
                date: z.string().trim().min(1, t('Date is required')).refine(isStrictIsoDate, t('Enter a valid event date')),
                valid_until: z.string().trim().min(1, t('Valid until is required')).refine(isStrictIsoDate, t('Enter a valid validity date')),
                adults: z
                    .number({
                        message: t('Adults must be a number'),
                    })
                    .int(t('Adults must be a whole number')).min(0, t('Adults must not be negative')).max(500, t('Adults must not exceed 500')),
                children: z
                    .number({
                        message: t('Children must be a number'),
                    })
                    .int(t('Children must be a whole number')).min(0, t('Children must not be negative')).max(500, t('Children must not exceed 500')),
                company_id: z.number({ message: t('Company is required') }).int().positive(t('Company is required')),
                contact_id: z
                    .union([
                        z.number().int().positive(t('Contact must be a valid ID')),
                        z.string().trim().regex(/^[1-9]\d*$/, t('Contact must be a valid ID')),
                        z.literal(''),
                    ])
                    .transform((value) => value === '' ? null : value)
                    .nullable()
                    .optional(),
                probability: z
                    .number({
                        message: t('Probability must be a number'),
                    })
                    .min(0, t('Probability must be between 0 and 100'))
                    .max(100, t('Probability must be between 0 and 100'))
                    .nullable()
                    .optional(),
                duration_minutes: z
                    .number({
                        message: t('Duration must be a number'),
                    })
                    .min(0)
                    .nullable()
                    .optional(),
            }).superRefine((data, ctx) => {
                if (data.adults + data.children < 1) ctx.addIssue({code: 'custom', path: ['adults'], message: t('Select at least one person')})
                if (isStrictIsoDate(data.date) && isStrictIsoDate(data.valid_until) && data.valid_until > data.date) {
                    ctx.addIssue({code: 'custom', path: ['valid_until'], message: t('Valid until must not be after the event date')})
                }
            }),
        [t]
    )
}

export type OfferBasicInputs = z.infer<ReturnType<typeof useOfferBasicSchema>>
